import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import React, { useState, useEffect, useMemo, useRef, useContext } from 'react';
import { apiRequest, getAuthImage } from '@/Helpers/general';
import { DT_REPORTS_FILTERS, DT_SHORT_ROWS_NO } from '@/Config/constants';
import debounce from 'lodash.debounce';
import DatatableTemplate from '@/Components/Templates/DatatableTemplate';
import { dateBodyTemplate } from '@/Components/Templates/DatatableColumnTemplates';
import { AppContext } from '@/Components/Context/AppContext';
import { classNames } from 'primereact/utils';
import { Calendar } from 'primereact/calendar';
import moment from 'moment';
import { ExcelIcon } from '@/Helpers/svg-icons';
import { Tooltip } from 'primereact/tooltip';
import {useGetCasesExport} from "@/Service/Api/V1/ApiHooks/Exports/useGetCasesExport";
import {GetCasesExportRequest} from "@/Stub/V1";
import {formatToApiDate} from "@/Util/formatToApiDate";
import {addDay, addMonth, format} from "@formkit/tempo";
import {downloadBlob} from "@/Util/downloadBlob";

const today = new Date();

/**
 * Reports datatable
 */
const ReportsDatatable = () => {
    const searchReportRef = useRef();
    const exportLinkRef = useRef();
    const detailedExportLinkRef = useRef();
    const { userPreferences, setUserPreferences } = useContext(AppContext);

    const [usersReports, setUsersReports] = useState([]);
    const [usersLoading, setUsersLoading] = useState(true);
    const [usersRowsPerPage, setUsersRowsPerPage] = useState(DT_SHORT_ROWS_NO);
    const [usersFirst, setUsersFirst] = useState(0);
    const [usersCurrentPage, setUsersCurrentPage] = useState(0);
    const [usersTotalRecords, setUsersTotalRecords] = useState(0);
    const [usersSortField, setUsersSortField] = useState('user');
    const [usersSortOrder, setUsersSortOrder] = useState(-1);

    const [datesReports, setDatesReports] = useState([]);
    const [datesLoading, setDatesLoading] = useState(true);
    const [datesRowsPerPage, setDatesRowsPerPage] = useState(DT_SHORT_ROWS_NO);
    const [datesFirst, setDatesFirst] = useState(0);
    const [datesCurrentPage, setDatesCurrentPage] = useState(0);
    const [datesTotalRecords, setDatesTotalRecords] = useState(0);
    const [datesSortField, setDatesSortField] = useState('date');
    const [datesSortOrder, setDatesSortOrder] = useState(-1);

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [startMinDate, setStartMinDate] = useState('');
    const [startMaxDate, setStartMaxDate] = useState('');
    const [endMinDate, setEndMinDate] = useState('');
    const [endMaxDate, setEndMaxDate] = useState('');
    const [searchReportInput, setSearchReportInput] = useState('');
    const [clearSearchIcon, setClearSearchIcon] = useState(false);
    const [exportUrl, setExportUrl] = useState();
    const [detailedExportUrl, setDetailedExportUrl] = useState();
    const [currentFilter, setCurrentFilter] = useState(userPreferences && userPreferences.reports_filter
        ? userPreferences.reports_filter
        : 'today'
    );

    const casesExportParams = useMemo<GetCasesExportRequest>(() => {
        if (startDate || endDate) {
            return {
                from: formatToApiDate(startDate),
                to: formatToApiDate(endDate)
            }
        }
        if (currentFilter === 'today') {
            return {
                from: formatToApiDate(today),
                to: formatToApiDate(today),
            }
        } else if (currentFilter === 'week') {
            return {
                from: formatToApiDate(addDay(today, -7)),
                to: formatToApiDate(today),
            }
        } else if (currentFilter === 'month') {
            return {
                from: formatToApiDate(addMonth(today, -1)),
                to: formatToApiDate(today),
            }
        }
        return {
            from: undefined,
            to: undefined,
        }
    }, [startDate, endDate, currentFilter]);

    const {
        refetch: casesExportRefetch
    } = useGetCasesExport({
        from: casesExportParams.from,
        to: casesExportParams.to,
        enabled: false
    })

    const onCasesExportDownload = async () => {
        const exportData = await casesExportRefetch();
        if (exportData.data) {
            downloadBlob(
                exportData.data,
                `cases_export_${format(today, 'YYYY-MM-DD_HH-mm-ss')}.csv`
            )
        }
    }

    /**
     * Generate the export file
     * 
     * @param {string} type "normal" or "detailed" export
     */
    const generateExport = async (type) => {
        let exportUrl = import.meta.env.VITE_API_URL + 'export/reports/agent-date' + (type == 'detailed' ? '-detail' : ''),
            linkRef = type == 'detailed' ? detailedExportLinkRef?.current : exportLinkRef?.current,
            requestParams = {};

        // search param
        if (searchReportInput && searchReportInput != '') {
            requestParams.search = searchReportInput;
        }

        // sort params
        if (usersSortField !== null) {
            requestParams.agent_sort_by = usersSortField;
        }

        if (usersSortOrder != 0) {
            requestParams.agent_sort_order = usersSortOrder === -1 ? 'desc' : 'asc';
        }

        if (datesSortField !== null) {
            requestParams.date_sort_by = datesSortField;
        }

        if (datesSortOrder != 0) {
            requestParams.date_sort_order = datesSortOrder === -1 ? 'desc' : 'asc';
        }

        // filter params
        requestParams.filter_by = currentFilter;

        // filter by start and end date
        if (startDate) {
            requestParams.start_date = moment(startDate).format('YYYY-MM-DD');
        }

        if (endDate) {
            requestParams.end_date = moment(endDate).format('YYYY-MM-DD');
        }

        exportUrl += '?' + Object.keys(requestParams).map(key => key + '=' + requestParams[key]).join('&');
        // console.log(exportUrl);

        const fileUrl = await getAuthImage(exportUrl);

        if (fileUrl && linkRef) {
            linkRef.href = fileUrl;
            linkRef.click();
        }
    };

    /**
     * Get reports by user from the api
     */
    const getUsersReports = () => {
        const usersRequestParams = {
            page: usersCurrentPage + 1,
            limit: usersRowsPerPage
        };
        setUsersLoading(true);

        // search param
        if (searchReportInput && searchReportInput != '') {
            usersRequestParams.search = searchReportInput;
            setClearSearchIcon(true);
        }

        // hide clear search icon
        if (searchReportInput == '') {
            setClearSearchIcon(false);
        }

        // sort params
        if (usersSortField !== null) {
            usersRequestParams.sort_by = usersSortField;
        }

        if (usersSortOrder != 0) {
            usersRequestParams.sort_order = usersSortOrder === -1 ? 'desc' : 'asc';
        }

        // filter params
        usersRequestParams.filter_by = currentFilter;

        // filter by start and end date
        if (startDate) {
            usersRequestParams.start_date = moment(startDate).format('YYYY-MM-DD');
        }

        if (endDate) {
            usersRequestParams.end_date = moment(endDate).format('YYYY-MM-DD');
        }

        apiRequest('get', 'reports/agent', {
            data: usersRequestParams,
            onSuccess: (response) => {
                setUsersLoading(false);

                if (response.result) {
                    setUsersReports(response.result);
                }

                if (response.pagination?.totalEntries) {
                    setUsersTotalRecords(response.pagination.totalEntries);
                }
            },
            onFailure: () => {
                setUsersLoading(false);
            }
        });
    };


    /**
     * Get reports by date from the api
     */
    const getDatesReports = () => {
        const datesRequestParams = {
            page: datesCurrentPage + 1,
            limit: datesRowsPerPage
        };
        setDatesLoading(true);

        // search param
        if (searchReportInput && searchReportInput != '') {
            datesRequestParams.search = searchReportInput;
            setClearSearchIcon(true);
        }

        // hide clear search icon
        if (searchReportInput == '') {
            setClearSearchIcon(false);
        }

        // sort params
        if (datesSortField !== null) {
            datesRequestParams.sort_by = datesSortField;
        }

        if (datesSortOrder != 0) {
            datesRequestParams.sort_order = datesSortOrder === -1 ? 'desc' : 'asc';
        }

        // filter params
        datesRequestParams.filter_by = currentFilter;

        // filter by start and end date
        if (startDate) {
            datesRequestParams.start_date = moment(startDate).format('YYYY-MM-DD');
        }

        if (endDate) {
            datesRequestParams.end_date = moment(endDate).format('YYYY-MM-DD');
        }

        apiRequest('get', 'reports/date', {
            data: datesRequestParams,
            onSuccess: (response) => {
                setDatesLoading(false);

                if (response.result) {
                    setDatesReports(response.result);
                }

                if (response.pagination?.totalEntries) {
                    setDatesTotalRecords(response.pagination.totalEntries);
                }
            },
            onFailure: () => {
                setDatesLoading(false);
            }
        });
    };

    /**
     * Search for a report
     * 
     * @param {object} e event object containing search data
     */
    const searchReport = (e) => {
        setSearchReportInput(e.target.value);
        setUsersFirst(0);
        setUsersCurrentPage(0);
        setDatesFirst(0);
        setDatesCurrentPage(0);
    };

    /**
     * Clear the report search
     */
    const clearReportSearch = () => {
        searchReportRef.current.value = '';
        setSearchReportInput('');
        setUsersFirst(0);
        setUsersCurrentPage(0);
        setDatesFirst(0);
        setDatesCurrentPage(0);
    };

    /**
     * Debounce the report search function
     */
    const debounceReportSearch = useMemo(() => {
        return debounce(searchReport, 500);
    }, []);

    /**
     * On sort column function
     * 
     * @param {object} e event object containing sort data
     * @param {string} table the name of the table
     */
    const onSort = (e, table) => {
        switch (table) {
            case 'users':
                setUsersSortField(e.sortField);
                setUsersSortOrder(e.sortOrder);
                setUsersFirst(0);
                setUsersCurrentPage(0);
                break;

            case 'dates':
                setDatesSortField(e.sortField);
                setDatesSortOrder(e.sortOrder);
                setDatesFirst(0);
                setDatesCurrentPage(0);
                break;

            default:
                break;
        }
    };

    /**
     * On change page function
     * 
     * @param {object} e event object containing current page related data
     * @param {string} table the name of the table
     */
    const onPageChange = (e, table) => {
        switch (table) {
            case 'users':
                setUsersCurrentPage(e.page);
                setUsersFirst(e.first);
                setUsersRowsPerPage(e.rows);
                break;

            case 'dates':
                setDatesCurrentPage(e.page);
                setDatesFirst(e.first);
                setDatesRowsPerPage(e.rows);
                break;

            default:
                break;
        }
    };

    /**
     * Change the start/end date
     * 
     * @param {e} object event object with data about the selected date
     * @param {string} type start or end
     */
    const changeDate = (e, type) => {
        const selectedDate = e?.target?.value;

        if (selectedDate) {
            switch (type) {
                case 'start':
                    setStartDate(selectedDate);
                    setEndMinDate(selectedDate);
                    break;

                case 'end':
                    setEndDate(selectedDate);
                    setStartMaxDate(selectedDate);
                    break;

                default:
                    break;
            }
        }
    };

    /**
     * Change the main filter
     * 
     * @param {string} newFilter new filter value
     */
    const changeFilter = (newFilter) => {
        const copyUserPreferences = { ...userPreferences };

        copyUserPreferences.reports_filter = newFilter;

        setCurrentFilter(newFilter);
        setUserPreferences(copyUserPreferences);
        setUsersFirst(0);
        setDatesFirst(0);
        setUsersCurrentPage(0);
        setDatesCurrentPage(0);
    };

    /**
     * The header of the datatable
     */
    const header = () => {
        return (
            <>
                <div className="filters-wrap flex mb-3">
                    {DT_REPORTS_FILTERS.map((filter) => {
                        return (
                            <div className={classNames({ 'filter': true, 'active': currentFilter == filter.key })}
                                onClick={() => {
                                    changeFilter(filter.key);
                                }}
                                key={filter.key}
                            >
                                <span>{filter.name}</span>
                            </div>
                        );
                    })}
                </div>

                <div className="flex">
                    <span className="p-input-icon-left search-input-wrap mr-6">
                        <i className="pi pi-search pl-2" />
                        <InputText className="pr-6 pl-4" onChange={debounceReportSearch} ref={searchReportRef} placeholder="Search" />
                        {clearSearchIcon && <i className="pi pi-times p-button p-button-sm p-button-danger p-button-text p-2" onClick={clearReportSearch} />}
                    </span>

                    <div className="dates-wrap mr-4">
                        <label htmlFor="start-date" className="mr-2">Date range:</label>

                        <div className="relative inline-block">
                            <Calendar id="start-date" value={startDate}
                                minDate={startMinDate}
                                maxDate={endDate ?? today}
                                placeholder="Start date"
                                onChange={(e) => changeDate(e, 'start')}
                                selectOtherMonths
                                showMinMaxRange
                                showButtonBar
                                dateFormat="dd/mm/yy"
                            />
                            <div className="calendar-icon-wrap">
                                <i className="pi pi-calendar"></i>
                            </div>
                        </div>
                        <span> - </span>
                        <div className="relative inline-block">
                            <Calendar id="end-date" value={endDate}
                                minDate={startDate}
                                maxDate={today}
                                placeholder="End date"
                                onChange={(e) => changeDate(e, 'end')}
                                selectOtherMonths
                                showMinMaxRange
                                showButtonBar
                                dateFormat="dd/mm/yy"
                            />
                            <div className="calendar-icon-wrap">
                                <i className="pi pi-calendar"></i>
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-wrap align-content-center">
                        <span id="generate-normal-export" className="fake-link mb-3" onClick={() => generateExport('normal')}>
                            <ExcelIcon className="mr-3" />
                        </span>
                        <span id="generate-detailed-export" className="fake-link" onClick={() => onCasesExportDownload()}>
                            <ExcelIcon className="mr-3" />
                        </span>
                        <a href="" className="hidden" target="_blank" ref={exportLinkRef}></a>
                        <a href="" className="hidden" target="_blank" ref={detailedExportLinkRef}></a>
                        <Tooltip target="#generate-normal-export" content="Download user report including all statuses for all cases" position="top" />
                        <Tooltip target="#generate-detailed-export" content="Download details report for each of the cases in the selected date range" position="top" />
                    </div>
                </div>
            </>
        );
    };

    /**
     * Get the reports by user again in case the search, page, rows number or sort has been changed
     */
    useEffect(() => {
        getUsersReports();
    }, [
        searchReportInput,
        usersCurrentPage,
        usersSortField,
        usersSortOrder,
        usersRowsPerPage,
        currentFilter,
        startDate,
        endDate
    ]);

    /**
     * Get the reports by date again in case the search, page, rows number or sort has been changed
     */
    useEffect(() => {
        getDatesReports();
    }, [
        searchReportInput,
        datesCurrentPage,
        datesSortField,
        datesSortOrder,
        datesRowsPerPage,
        currentFilter,
        startDate,
        endDate
    ]);

    /**
     * Clear the debounce on component unmount
     */
    useEffect(() => {
        return () => {
            debounceReportSearch.cancel();
        };
    });

    return (
        <>
            <DatatableTemplate
                items={usersReports}
                header={header}
                rowsPerPage={usersRowsPerPage}
                loading={usersLoading}
                emptyMessage="No reports found."
                totalRecords={usersTotalRecords}
                first={usersFirst}
                onPageChange={(e) => onPageChange(e, 'users')}
                customProps={{
                    className: 'short-dt mb-5',
                    sortField: usersSortField,
                    sortOrder: usersSortOrder,
                    onSort: (e) => onSort(e, 'users')
                }}
                paginator={usersTotalRecords>usersRowsPerPage}
                shortTable
            >
                <Column field="user" header="Name" sortable />
                <Column field="assigned" header="Assigned" sortable />
                <Column field="in_progress" header="In Progress" sortable />
                <Column field="on_hold" header="On hold" sortable />
                <Column field="complete" header="Complete" sortable />
            </DatatableTemplate>

            <DatatableTemplate
                items={datesReports}
                header={null}
                rowsPerPage={datesRowsPerPage}
                loading={datesLoading}
                emptyMessage="No reports found."
                totalRecords={datesTotalRecords}
                first={datesFirst}
                onPageChange={(e) => onPageChange(e, 'dates')}
                customProps={{
                    className: 'short-dt',
                    sortField: datesSortField,
                    sortOrder: datesSortOrder,
                    onSort: (e) => onSort(e, 'dates')
                }}
                paginator={datesTotalRecords>datesRowsPerPage}
                shortTable
            >
                <Column field="date" header="Date" sortable body={(data) => dateBodyTemplate(data.date, true)} />
                <Column field="received" header="Received" sortable />
                <Column field="in_progress" header="In Progress" sortable />
                <Column field="on_hold" header="On hold" sortable />
                <Column field="outcome_pending_updates" header="Outcome Pending Updates" sortable />
                <Column field="cho_response_sent" header="CHO Response Sent" sortable />
                <Column field="complete" header="Complete" sortable />
            </DatatableTemplate>
        </>
    );
};

export default ReportsDatatable;